import { queryOptions } from '@tanstack/react-query'
import type { Tables } from '@quoter/shared/generated/db-types'
import { supabase } from '../lib/supabase'
import { getProduct } from '../config'

export const sessionKeys = {
  supabaseSession: ['supabase-session'] as const,
  profile: (uid: string) => ['profile', uid] as const,
  pricingPlans: ['pricing-plans'] as const,
  product: ['product'] as const,
}

/**
 * The signed-in user as this product sees them: their membership row, under
 * the field names the rest of the app has always used (`id` is the user id).
 * For the first product the row mirrors `profiles`; for every other product
 * the membership is the only user table there is.
 */
export type Profile = {
  id: string
  organization_id: string
  email: string
  full_name: string
  role: Tables<'memberships'>['role']
  active: boolean
  locale: string | null
  created_at: string
  updated_at: string
}

// staleTime Infinity: after the first read, this cache entry is only ever
// written by AuthStateBridge via setQueryData, so refetching would race the
// auth listener rather than help.
export const supabaseSessionQueryOptions = queryOptions({
  queryKey: sessionKeys.supabaseSession,
  queryFn: async () => {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw error
    return data.session // null = signed out
  },
  staleTime: Infinity,
})

/**
 * Membership in this product + the org's subscription status, in one query.
 *
 * The product filter is what keeps a user's workspace in another product out
 * of this app. Subscription status comes from a SECURITY DEFINER RPC rather
 * than a direct select, so members (not just admins) can be gated on it
 * without widening the table's SELECT policy; it reads the per-product claim.
 */
export function profileQueryOptions(userId: string | null) {
  return queryOptions({
    queryKey: sessionKeys.profile(userId ?? '__anon'),
    queryFn: async (): Promise<{ profile: Profile; subscriptionStatus: string | null } | null> => {
      const productId = getProduct().id
      const { data: row, error } = await supabase
        .from('memberships')
        .select('*')
        .eq('user_id', userId!)
        .eq('product_id', productId)
        .eq('active', true)
        .maybeSingle()
      if (error) throw error
      if (!row) return null

      const { data: status, error: statusError } = await supabase.rpc(
        'current_org_subscription_status',
        { p_product: productId },
      )
      if (statusError) throw statusError

      const { user_id, product_id: _product, ...rest } = row
      return { profile: { id: user_id, ...rest }, subscriptionStatus: (status as string | null) ?? null }
    },
    enabled: userId !== null,
    staleTime: 300_000,
  })
}

export type PricingPlan = {
  id: string
  name: string
  description: string | null
  price_cents: number
  currency: string
  interval: 'month' | 'year'
  features: string[]
  sort_order: number
}

export const pricingPlansQueryOptions = queryOptions({
  queryKey: sessionKeys.pricingPlans,
  queryFn: async () => {
    const { data, error } = await supabase
      .from('pricing_plans')
      .select('id, name, description, price_cents, currency, interval, features, sort_order')
      .eq('product_id', getProduct().id)
      .eq('active', true)
      .order('sort_order')
    if (error) throw error
    return (data ?? []) as unknown as PricingPlan[]
  },
  staleTime: 300_000,
})

/** This product's registry row — public, so it loads before sign-in. Carries the trial length Checkout applies. */
export const productQueryOptions = queryOptions({
  queryKey: sessionKeys.product,
  queryFn: async () => {
    const { data, error } = await supabase
      .from('products')
      .select('id, name, trial_days')
      .eq('id', getProduct().id)
      .maybeSingle()
    if (error) throw error
    return data
  },
  staleTime: 3_600_000,
})
